import * as React from 'react';
import { observer } from 'mobx-react';
import { configConsumer } from '@estee/elc-service';
import { SliderView, ISliderSettings, ISliderBreakpoints } from '@estee/elc-slider';
import { Container } from '@estee/elc-grid';
import styled from 'styled-components';
import { Breakpoints, ContentSpacing } from '@estee/elc-base-theme';
import { IContentItem } from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { Product } from '../../domain/entities/Product';
import { GridItem } from '../../domain/entities/GridItem';
import { DEFAULT_CAROUSEL_SETTINGS, gridItemType } from '../../constants/ProductGrid';
import { CollectionItem, CollectionItemType } from '../product-grid/CollectionItem';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { classes } from '../../utils/Classes';
import { PRODUCT_CAROUSEL } from '~constants/DataTestIdAttributes';
import { getResponsiveConfig } from '~utils/ConfigUtils';

export interface IProductCarousel {
    config: {
        products?: Product[];
        gridItems?: GridItem[];
        content?: IContentItem[];
        carouselSettings?: ISliderSettings;
        defaultCarouselSettings?: ISliderSettings;
        toutWidth?: number;
        toutHeight?: number;
        loadCarouselFirstSlide?: boolean;
        forceLazyImageLoading?: boolean;
    };
    className?: string;
    disableFullHeight?: boolean;
}

const SliderViewStyled = styled.div`
    margin-bottom: ${ContentSpacing.space20};

    .slick-track {
        margin-left: 0;
        padding: ${ContentSpacing.space4} 0;
        display: flex;
    }

    .slick-slide {
        & > div {
            height: ${(props) => (props.theme.disableFullHeight ? 'auto' : '100%')};
        }

        height: auto;
        float: none;
        margin: 0 ${ContentSpacing.space4};
    }

    [dir='rtl'] .slick-track {
        margin-right: 0;
        margin-left: auto;
    }

    .elc-slider-arrow-wrapper {
        &::before {
            content: none;
        }
        &.slick-prev {
            left: -15px;
        }

        [dir='rtl'] &.slick-prev {
            left: 0;
            right: -15px;
        }
    }
`;
@configConsumer(['loadCarouselFirstSlide'])
@observer
export class ProductCarousel extends React.Component<IProductCarousel> {
    public static displayName: string;

    public overrideCarouselSettings = () => {
        const { carouselSettings, defaultCarouselSettings, loadCarouselFirstSlide } =
            this.props.config;

        if (!carouselSettings) {
            return defaultCarouselSettings || DEFAULT_CAROUSEL_SETTINGS;
        }

        const { responsive: responsiveDefault, ...restDefaultProperties } =
            DEFAULT_CAROUSEL_SETTINGS;

        const { responsive: responsiveOverrideSettings, ...restOverrideProperties } =
            carouselSettings;

        const restSettings = {
            ...restDefaultProperties,
            ...restOverrideProperties
        };

        if (!responsiveOverrideSettings) {
            const settings = {
                ...restSettings,
                responsive: responsiveDefault
            };

            return { ...settings };
        }
        const responsiveSettings = responsiveDefault.map(function (responsive, index) {
            const responsiveOverrides =
                getResponsiveConfig(responsive.breakpoint, responsiveOverrideSettings) || {};

            return {
                ...responsive,
                settings: {
                    ...responsive.settings,
                    ...responsiveOverrides
                }
            };
        });

        const finalSettings = {
            ...restSettings,
            lazyLoad: loadCarouselFirstSlide ? 'ondemand' : null,
            responsive: responsiveSettings
        };

        return { ...finalSettings };
    };

    public mobileCarouselSettings = (carouselSettings: ISliderSettings): ISliderBreakpoints =>
        carouselSettings?.responsive?.find(
            (setting) => setting.breakpoint === Breakpoints.desktop
        ) as ISliderBreakpoints;

    private lazyLoadImage = (positionIndex: number, carouselSettings: ISliderSettings) => {
        const { forceLazyImageLoading } = this.props.config;

        if (forceLazyImageLoading) {
            return {
                forceLazyLoadImage: true,
                lazyLoadImageMobile: true,
                lazyLoadImageDesktop: true
            };
        }

        const mobileSettings = this.mobileCarouselSettings(carouselSettings);

        const lazyLoadImageMobile = positionIndex >= Number(mobileSettings.settings.slidesToShow);
        const lazyLoadImageDesktop = positionIndex >= Number(carouselSettings.slidesToShow);

        return {
            forceLazyLoadImage: false,
            lazyLoadImageMobile,
            lazyLoadImageDesktop
        };
    };

    private renderProductItem = (
        product: Product,
        carouselSettings: ISliderSettings,
        positionIndex: number
    ) => {
        const { lazyLoadImageMobile, lazyLoadImageDesktop, forceLazyLoadImage } =
            this.lazyLoadImage(positionIndex, carouselSettings);

        return (
            product && (
                <CollectionItem
                    key={`product-item-${product.productId}`}
                    id={product.productId}
                    collectionItem={CollectionItemType.PRODUCT}
                    productBriefProps={{
                        componentType: ComponentTypes.PRODUCT_BRIEF,
                        product,
                        forceLazyLoadImage,
                        lazyLoadImageMobile,
                        lazyLoadImageDesktop
                    }}
                />
            )
        );
    };

    private renderGridItems(
        item: GridItem,
        carouselSettings: ISliderSettings,
        positionIndex: number
    ) {
        const { lazyLoadImageMobile, lazyLoadImageDesktop } = this.lazyLoadImage(
            positionIndex,
            carouselSettings
        );
        switch (item.itemType) {
            case gridItemType.product:
            case gridItemType.featuredProduct:
                return item.product ? (
                    <CollectionItem
                        key={`grid-item-${item.itemId}`}
                        id={item.itemId}
                        collectionItem={CollectionItemType.PRODUCT}
                        productBriefProps={{
                            componentType: ComponentTypes.PRODUCT_BRIEF,
                            product: item.product,
                            lazyLoadImageMobile,
                            lazyLoadImageDesktop
                        }}
                    />
                ) : (
                    <></>
                );
            default:
                return (
                    <CollectionItem
                        key={`default-item-${item.itemId}`}
                        id={item.itemId}
                        collectionItem={CollectionItemType.TOUT}
                    />
                );
        }
    }

    public render() {
        const carouselContainerClassNames = classes('product-carousel-container');
        const carouselSettings = this.overrideCarouselSettings();
        const {
            disableFullHeight,
            config: { gridItems, products }
        } = this.props;

        let carouselItems: React.ReactElement[] = [];

        if (gridItems && gridItems.length > 0) {
            carouselItems = gridItems.reduce(
                (accumulator: React.ReactElement[], item, currentIndex) => {
                    if (item.itemType !== gridItemType.featuredProduct) {
                        accumulator.push(
                            this.renderGridItems(item, carouselSettings, currentIndex)
                        );
                    }

                    return accumulator;
                },
                []
            );
        } else if (products && products.length > 0) {
            carouselItems = products.map((product, index) =>
                this.renderProductItem(product, carouselSettings, index)
            );
        }

        return (
            carouselItems.length > 0 && (
                <Container data-test-id={PRODUCT_CAROUSEL} className={carouselContainerClassNames}>
                    <SliderViewStyled
                        className="elc-product-carousel-slider-view"
                        theme={{ disableFullHeight }}
                    >
                        <SliderView sliderSettings={carouselSettings} items={carouselItems} />
                    </SliderViewStyled>
                </Container>
            )
        );
    }
}

ProductCarousel.displayName = 'ProductCarousel';
